import React, { useState } from "react";
import {
  Button,
  Box,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  Grid,
  Typography,
  CircularProgress,
  Switch,
  Divider,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ToggleOnIcon from "@mui/icons-material/ToggleOn";
import ToggleOffIcon from "@mui/icons-material/ToggleOff";

import GenericMUITable from "../../../../components/GenericTable";

const CardProductTable = ({ data, pagination, onToggleDefault }) => {
  const [viewItem, setViewItem] = useState(null);
  const [togglingId, setTogglingId] = useState(null);

  const handleToggleDefault = async (item) => {
    if (!item?.id) return;
    if (typeof onToggleDefault === "function") {
      try {
        setTogglingId(item.id);
        const res = onToggleDefault(item);
        if (res && typeof res.then === "function") {
          await res;
        }
      } finally {
        setTogglingId(null);
      }
    } else {
      console.log("toggle default for", item.id);
    }
  };

  const openView = (item) => setViewItem(item);
  const closeView = () => setViewItem(null);

  const roleFields = [
    { key: "id", label: "UUID" },
    { key: "productName", label: "Product Name" },
    { key: "productCode", label: "Product Code" },
    { key: "bin", label: "BIN" },
    {
      key: "status",
      label: "Status",
      render: (item) => (item?.status ? item.status.toUpperCase() : ""),
    },
    {
      key: "default",
      label: "Default",
      render: (item) => {
        const isDefault = !!item.default;
        const isBusy = togglingId === item.id;
        return (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Switch
              checked={isDefault}
              onChange={() => handleToggleDefault(item)}
              disabled={isBusy}
              inputProps={{ "aria-label": isDefault ? "Default product" : "Set default" }}
            />
            {isBusy && <CircularProgress size={16} />}
          </Box>
        );
      },
    },
    {
      key: "actions",
      label: "Actions",
      render: (item) => (
        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
          <Button
            size="small"
            onClick={() => openView(item)}
            aria-label={`View ${item.productName}`}
            sx={{ minWidth: "auto", p: "4px" }}
          >
            <VisibilityIcon sx={{ color: "#828282" }} />
          </Button>
        </Box>
      ),
    },
  ];
  return (
    <>
      <GenericMUITable fields={roleFields} data={data} rowKey="id" pagination={pagination} />

      <Dialog open={!!viewItem} onClose={closeView} maxWidth="md" fullWidth>
        <DialogTitle
          sx={{
            fontWeight: 600,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          Card Product Details
          <IconButton onClick={closeView} aria-label="close">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ p: 3 }}>
          {viewItem && (
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2">UUID</Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    {viewItem.id}
                  </Typography>
                </Box>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2">Product Name</Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    {viewItem.productName || "-"}
                  </Typography>
                </Box>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2">Product Code</Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    {viewItem.productCode || "-"}
                  </Typography>
                </Box>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2">BIN</Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    {viewItem.bin || "-"}
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12} md={6}>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2">Status</Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    {viewItem.status || "-"}
                  </Typography>
                </Box>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2">Default</Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    {viewItem.default ? "Yes" : "No"}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", gap: 2, mt: 1 }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle2">Front Image</Typography>
                    {viewItem.frontImage ? (
                      <Box
                        component="img"
                        src={viewItem.frontImage}
                        alt="front"
                        sx={{ width: "100%", maxHeight: 140, objectFit: "contain", mb: 1 }}
                      />
                    ) : (
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        -
                      </Typography>
                    )}
                  </Box>

                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle2">Back Image</Typography>
                    {viewItem.backImage ? (
                      <Box
                        component="img"
                        src={viewItem.backImage}
                        alt="back"
                        sx={{ width: "100%", maxHeight: 140, objectFit: "contain", mb: 1 }}
                      />
                    ) : (
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        -
                      </Typography>
                    )}
                  </Box>
                </Box>
              </Grid>
            </Grid>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default CardProductTable;
